import * as Dialog from '@radix-ui/react-dialog';
import * as Tabs from '@radix-ui/react-tabs';
import type { ReactNode } from 'react';
import { Icone } from './Icone.js';

/**
 * Modal sobre Radix Dialog.
 *
 * A escolha do Radix é por acessibilidade, não por conveniência. O
 * protótipo já entrega ARIA correto (`role`, `aria-selected`,
 * `aria-pressed`, `aria-current`) e foco visível; o que ele NÃO tem,
 * porque é protótipo, é armadilha de foco, restauração de foco ao
 * fechar, `aria-modal` e o Esc.
 *
 * Reimplementar isso à mão é onde a acessibilidade morre em silêncio —
 * o modal parece funcionar com mouse e prende quem usa teclado. O
 * Radix entrega os quatro e recebe as classes do protótipo por cima.
 *
 * Ver: Arena_backend/Docs/plano/05-front-fundacao.md
 */

export function Modal({
  aberto,
  onFechar,
  titulo,
  descricao,
  marca,
  largura = 'media',
  rodape,
  children,
}: {
  aberto: boolean;
  onFechar: () => void;
  titulo: ReactNode;
  descricao?: ReactNode;
  /** O quadradinho colorido antes do título — `#m-evento`, `#m-conflito`. */
  marca?: ReactNode;
  largura?: 'estreita' | 'media' | 'larga';
  rodape?: ReactNode;
  children: ReactNode;
}) {
  const classeDeLargura = { estreita: 'slim', media: '', larga: 'wide' }[largura];

  return (
    <Dialog.Root open={aberto} onOpenChange={(estado) => !estado && onFechar()}>
      <Dialog.Portal>
        <Dialog.Overlay className="scrim">
          <Dialog.Content className={['modal', classeDeLargura].filter(Boolean).join(' ')}>
            <header className="m-head">
              {marca}
              <div>
                <Dialog.Title asChild>
                  <h3>{titulo}</h3>
                </Dialog.Title>
                {descricao ? (
                  <Dialog.Description asChild>
                    <p>{descricao}</p>
                  </Dialog.Description>
                ) : (
                  // O Radix avisa no console quando falta descrição.
                  // Dizer explicitamente que não há é melhor que o aviso.
                  <Dialog.Description className="sr-only" />
                )}
              </div>
              <Dialog.Close asChild>
                <button type="button" className="x" aria-label="Fechar">
                  <Icone nome="x" />
                </button>
              </Dialog.Close>
            </header>

            {children}

            {rodape && <footer className="m-foot">{rodape}</footer>}
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** O corpo rolável do modal. Separado para conviver com as abas. */
export function CorpoDeModal({ children }: { children: ReactNode }) {
  return <div className="m-body sb">{children}</div>;
}

/**
 * As abas de `#m-aluno` — Dados, Financeiro, Presenças, LGPD.
 *
 * Ficam entre o cabeçalho e o corpo, como no protótipo: a régua das
 * abas encosta na borda do cabeçalho e o conteúdo rola por baixo.
 */
export function AbasDeModal({
  abas,
  inicial,
}: {
  abas: readonly { chave: string; rotulo: string; conteudo: ReactNode }[];
  inicial?: string;
}) {
  const primeira = abas[0];
  if (!primeira) return null;

  return (
    <Tabs.Root defaultValue={inicial ?? primeira.chave}>
      <Tabs.List className="tabs">
        {abas.map((a) => (
          <Tabs.Trigger key={a.chave} value={a.chave}>
            {a.rotulo}
          </Tabs.Trigger>
        ))}
      </Tabs.List>
      {abas.map((a) => (
        <Tabs.Content key={a.chave} value={a.chave} className="m-body sb">
          <div className="tabpane">{a.conteudo}</div>
        </Tabs.Content>
      ))}
    </Tabs.Root>
  );
}
